import { Button, Link as ChakraLink, Flex, useBreakpointValue } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { FilterBar } from '@resistapp/client/components/bars/filter-bar/filter-bar';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { BarBoxMap } from '@resistapp/client/components/plots/bar-box-map/bar-box-map';
import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { theme } from '@resistapp/client/components/shared/theme';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { ChartDownloadProvider } from '@resistapp/client/contexts/chart-download-context';
import { ContextualTipProvider } from '@resistapp/client/contexts/contextual-tip-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { isExtraWideProject } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { useDeleteProject } from '@resistapp/client/hooks/api';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { useToastWithDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { getCtrlKeyName } from '@resistapp/client/utils/general';
import { hasResearchAccessForProject, isNonDemo } from '@resistapp/common/features';
import { keys } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Sticky from 'react-stickynode';
import useLocalStorageState from 'use-local-storage-state';
import { AdminSection, ProjectDeletionMode } from './admin-section';
import { MethodAndDownloadSections } from './method-section';

const FilterBarMargin = {
  sticky: '0px 25px',
  nonSticky: '0px 35px',
};

export function ResearchView() {
  const { project, loading, error: sampleDataError, adminFeatures } = useResearchContext();
  const { assaysLoaded } = useAssayContext();
  const navigate = useNavigateWithQuery();
  const { isAdmin, user } = useUser();
  const hasResearchAccess = hasResearchAccessForProject(user, project?.id);
  const deleteProject = useDeleteProject(project?.id || 0);
  const { data: deleted } = deleteProject;
  const showToast = useToastWithDebounce();
  const stickyChildRef = useRef<HTMLDivElement>(null);
  // Only activate sticky if the display screen is desktop
  const isSticky = useBreakpointValue({ base: false, lg: true });

  const [notifiedAssayVersion, setNotifiedAssayVersion] = useLocalStorageState<string>(
    LocalStorageId.notifiedAssayVersion,
    {
      defaultValue: 'v3.0.1', // Previous version at time of notification feature launch
    },
  );

  const [welcomeToastShown, setWelcomeToastShown] = useLocalStorageState<boolean>(
    LocalStorageId.researchViewWelcomeShown,
    {
      defaultValue: false,
    },
  );

  useEffect(() => {
    if (
      hasResearchAccess &&
      isNonDemo(user) &&
      project?.assayVersion &&
      project.assayChangeNotification &&
      project.assayVersion !== notifiedAssayVersion &&
      process.env.NODE_ENV !== 'development' // disable in visual tests
    ) {
      showToast({
        title: 'New Results Available',
        description: (
          <span>
            {project.assayChangeNotification}{' '}
            <ChakraLink as={RouterLink} to="/assay-log" color="blue.400" textDecoration="underline">
              View&nbsp;change&nbsp;log
            </ChakraLink>
          </span>
        ),
        status: 'info',
        duration: null,
        isClosable: true,
      });
      setNotifiedAssayVersion(project.assayVersion);
    }
  }, [
    hasResearchAccess,
    project?.assayVersion,
    project?.assayChangeNotification,
    showToast,
    user,
    notifiedAssayVersion,
    setNotifiedAssayVersion,
  ]);

  useEffect(() => {
    if (hasResearchAccess && !welcomeToastShown) {
      const ctrlKey = getCtrlKeyName();
      showToast({
        title: 'Welcome to Resistomap Platform!',
        description:
          'Hover on graphs, or click on legends and filters to explore the data.\n\n' +
          `Hold ${ctrlKey} key while clicking on the left-side legend panels` +
          `\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\n\n` +
          `to select multiple samples or gene classes.`,
        status: 'info',
        duration: null,
        isClosable: true,
        onCloseComplete: () => {
          setWelcomeToastShown(true);
        },
      });
    }
  }, [hasResearchAccess, showToast, welcomeToastShown, setWelcomeToastShown]);

  const triggerDelete = useCallback(
    async (mode: ProjectDeletionMode) => {
      const response =
        mode === 'shallow-revoke' ? await deleteProject.shallowRevoke() : await deleteProject.shallowKeep();
      const isSuccess = response.status !== undefined && response.status >= 200 && response.status <= 204;
      if (!isSuccess) {
        const serverMessage = (response.data as unknown as { message?: string } | undefined)?.message;
        const message = serverMessage || response.statusText || 'Failed to delete project';
        showToast({ title: 'Project deletion failed', description: message, status: 'error' });
        throw new Error(message);
      }
      navigate('/', false);
    },
    [deleteProject, navigate, showToast],
  );

  useEffect(() => {
    if (deleted) {
      navigate(`/admin`, false);
    }
  }, [deleted, navigate]);

  const error = sampleDataError;
  if (error) {
    throw error;
  }
  if (loading || !assaysLoaded) {
    return <LoadingView />;
  }

  const hasSamples = keys(project.samplesByUID).length > 0;

  // TODO fix legacy projects that do not have heatmaps
  const extraWide = isExtraWideProject(project.samplesByUID);

  if (!hasResearchAccess) {
    return (
      <div style={{ marginBottom: oldTheme.spacing.xl }} id="research-view">
        <Root>
          <TitleBar $extraWide={extraWide} title={<span>{project.name} </span>} />
        </Root>
        <div style={{ marginLeft: '20px', marginRight: '20px' }}>
          {project.id && <MethodAndDownloadSections project={project} displayMode="ct-only" />}
        </div>
      </div>
    );
  }

  return (
    <ContextualTipProvider>
      <ChartDownloadProvider>
        <div style={{ marginBottom: oldTheme.spacing.xl }} id="research-view">
          <Root>
            <TitleBar $extraWide={extraWide} title={<span>{project.name} </span>} />
            <Sticky
              enabled={isSticky}
              onStateChange={newStatus => {
                // This was fixed with manual styles, since Sticky was lagging, when using react state changes
                if (stickyChildRef.current) {
                  stickyChildRef.current.style.borderBottom =
                    newStatus.status !== Sticky.STATUS_ORIGINAL ? `1px solid ${theme.colors.neutral300}` : '';
                  stickyChildRef.current.style.margin =
                    newStatus.status !== Sticky.STATUS_ORIGINAL ? FilterBarMargin.sticky : FilterBarMargin.nonSticky;
                  stickyChildRef.current.style.boxShadow =
                    newStatus.status !== Sticky.STATUS_ORIGINAL ? '0px 2px 20px 0px rgba(41, 54, 81, 0.15)' : 'none';
                }
              }}
            >
              <div style={{ height: '25px' }} />
              <FilterBar
                innerRef={stickyChildRef}
                style={{
                  transition: 'margin 1s 0s',
                  boxShadow: 'none',
                  margin: FilterBarMargin.nonSticky,
                }}
                project={hasSamples ? project : null}
              />
            </Sticky>
          </Root>
          <div style={{ marginLeft: '20px', marginRight: '20px' }}>
            {hasSamples ? (
              <BarBoxMap />
            ) : (
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>This project has no samples</div>
            )}

            {project.id && <MethodAndDownloadSections project={project} />}
            {project.id && isAdmin && (
              <>
                <Flex justifyContent="center" paddingTop="20px">
                  <Button
                    onClick={() => {
                      adminFeatures.setIsModifyAsAdminActive(!adminFeatures.isModifyAsAdminActive);
                    }}
                    variant={adminFeatures.isModifyAsAdminActive ? 'green' : 'red'}
                  >
                    {adminFeatures.isModifyAsAdminActive ? 'Switch to normal mode' : 'Switch to admin mode'}
                  </Button>
                </Flex>
                {adminFeatures.isModifyAsAdminActive && (
                  <AdminSection project={project} triggerDelete={triggerDelete} />
                )}
              </>
            )}
          </div>
        </div>
      </ChartDownloadProvider>
    </ContextualTipProvider>
  );
}

const Root = styled.div`
  position: relative;
  z-index: ${theme.zIndexes.header};
`;
